import { CheckboxAdapter } from 'components/enhanced/form/adapters/CheckboxAdapter';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { required } from 'components/helpers/validators';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ISupplier } from '../types';
import SupplierIntegrationFormatFields from './SupplierIntegrationFormatFields';
import { italianDistricts } from 'shared/constant';
import { ORDER_SEND_FORMAT_VALUES, RECEPTION_FORMAT_VALUES, CSV_FORMAT_VALUES } from '../constants';
import { TextFieldAdapter } from 'components/enhanced';
import { SectionHeader } from 'components/ui/typography';

interface SupplierFormProps {
  supplier: ISupplier;
  onSubmit: any;
  disableEdit?: boolean;
}

function SupplierForm({ supplier, onSubmit, disableEdit }: SupplierFormProps) {
  const { t } = useTranslation();

  const orderSendFormatOptions = useMemo(
    () =>
      ORDER_SEND_FORMAT_VALUES.map((value) => ({
        value,
        display_name: t(`entities.supplier.order_send_format_options.${value}`)
      })),
    [t]
  );

  const receptionFormatOptions = useMemo(
    () =>
      RECEPTION_FORMAT_VALUES.map((value) => ({
        value,
        display_name: t(`entities.supplier.reception_format_options.${value}`)
      })),
    [t]
  );

  const csvFormatOptions = useMemo(
    () =>
      CSV_FORMAT_VALUES.map((value) => ({
        value,
        display_name: t(`entities.supplier.csv_format_options.${value}`)
      })),
    [t]
  );

  const resolveOptionInit = (
    options: { value: string; display_name: string }[],
    currentValue: string | undefined,
    defaultValue: string
  ) => options.find((o) => o.value === (currentValue ?? defaultValue)) ?? options[0];
  return (
    <div>
      {supplier && (
        <EnhancedForm
          onSubmit={disableEdit ? {} : onSubmit}
          edit={!!supplier.id}
          disableEdit={disableEdit}
          autoStyleLayout
          columns={12}>
          <SectionHeader title={t('pages.supplier.new.registry')} gridSpan />
          <IdentityField
            gridSpan={4}
            name="code"
            initialValue={supplier.code}
            component={TextFieldAdapter}
            label={t('global.code')}
            placeholder={t('global.code')}
            type="text"
            validate={required}
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={6}
            name="company_name"
            initialValue={supplier.company_name}
            component={TextFieldAdapter}
            label={t('entities.supplier.company_name')}
            placeholder={t('entities.supplier.company_name')}
            type="text"
            validate={required}
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={2}
            name="active"
            component={CheckboxAdapter}
            initialValue={supplier.active}
            label={t('global.active')}
            type="checkbox"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={5}
            name="city"
            initialValue={supplier.city}
            component={TextFieldAdapter}
            label={t('entities.supplier.city')}
            placeholder={t('entities.supplier.city')}
            type="text"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={5}
            name="district"
            component={AutocompleteAdapter}
            initValue={italianDistricts.find((s) => s.code === supplier.district)}
            options={italianDistricts}
            optionValue={'code'}
            placeholder={t('entities.supplier.district')}
            type="text"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={2}
            name="zip_code"
            initialValue={supplier.zip_code}
            component={TextFieldAdapter}
            label={t('entities.supplier.zip_code')}
            placeholder={t('entities.supplier.zip_code')}
            type="text"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={6}
            name="address"
            initialValue={supplier.address}
            component={TextFieldAdapter}
            label={t('entities.supplier.address')}
            placeholder={t('entities.supplier.address')}
            type="text"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={6}
            name="address_2"
            initialValue={supplier.address_2}
            component={TextFieldAdapter}
            label={t('entities.supplier.address_2')}
            placeholder={t('entities.supplier.address_2')}
            type="text"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={6}
            name="vat_number"
            initialValue={supplier.vat_number}
            component={TextFieldAdapter}
            label={t('entities.supplier.vat_number')}
            placeholder={t('entities.supplier.vat_number')}
            type="text"
            inputProps={{ pattern: '[0-9]{5,15}' }}
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={6}
            name="fiscal_code"
            initialValue={supplier.fiscal_code}
            component={TextFieldAdapter}
            label={t('entities.supplier.fiscal_code')}
            placeholder={t('entities.supplier.fiscal_code')}
            type="text"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={12}
            name="notes"
            label={t(`global.notes`)}
            placeholder={t(`global.notes`)}
            initialValue={supplier.notes}
            component={TextFieldAdapter}
            disabled={disableEdit}
          />

          <SectionHeader title={t('pages.supplier.new.contact')} gridSpan />

          <IdentityField
            gridSpan={6}
            name="email"
            initialValue={supplier.email}
            component={TextFieldAdapter}
            label={t('entities.supplier.email')}
            placeholder={t('entities.supplier.email')}
            type="email"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={6}
            name="email_credit_note"
            initialValue={supplier.email_credit_note}
            component={TextFieldAdapter}
            label={t('entities.supplier.email_credit_note')}
            type="email"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={4}
            name="phone_number"
            initialValue={supplier.phone_number}
            component={TextFieldAdapter}
            label={t('entities.supplier.phone_number')}
            placeholder={t('entities.supplier.phone_number')}
            type="text"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={4}
            name="fax_number"
            initialValue={supplier.fax_number}
            component={TextFieldAdapter}
            label={t('entities.supplier.fax_number')}
            placeholder={t('entities.supplier.fax_number')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="documentation_url"
            initialValue={supplier.documentation_url}
            component={TextFieldAdapter}
            label={t('entities.supplier.documentation_url')}
            placeholder="https://"
            type="url"
            disabled={disableEdit}
          />

          <SectionHeader title={t('pages.supplier.new.payment')} gridSpan />

          <IdentityField
            gridSpan={4}
            name="iban"
            initialValue={supplier.iban}
            component={TextFieldAdapter}
            label={t('entities.supplier.iban')}
            placeholder={t('entities.supplier.iban')}
            type="text"
            disabled={disableEdit}
          />

          <IdentityField
            gridSpan={8}
            name="payment_terms"
            initialValue={supplier.payment_terms}
            component={TextFieldAdapter}
            label={t('entities.supplier.payment_terms')}
            placeholder={t('entities.supplier.payment_terms')}
            type="text"
            disabled={disableEdit}
          />

          <SectionHeader title={t('pages.supplier.new.integration')} gridSpan />

          <SupplierIntegrationFormatFields
            supplier={supplier}
            disableEdit={disableEdit}
            orderSendFormatOptions={orderSendFormatOptions}
            receptionFormatOptions={receptionFormatOptions}
            csvFormatOptions={csvFormatOptions}
            resolveOptionInit={resolveOptionInit}
          />
        </EnhancedForm>
      )}
    </div>
  );
}

export default SupplierForm;
